<!-- ── launcher button ─────────────────────────────────────────────────── -->
<!-- Docked in the footer band, so it never covers a row's open-exam arrow. -->
<button class="sc-launcher" (click)="toggle()" [class.sc-launcher--open]="isOpen"
        [class.sc-launcher--unread]="!isOpen && unreadCount > 0" aria-label="Support chat">
  <span class="sc-launcher-icon" [class.sc-launcher-icon--close]="isOpen">
    @if (!isOpen) {
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
        <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
      </svg>
    } @else {
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
        <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
      </svg>
    }
  </span>
  <span class="sc-launcher-label">Support chat</span>
  @if (!isOpen && unreadCount > 0) {
    <span class="sc-unread-badge">{{ unreadCount }}</span>
  }
</button>

<!-- ── chat panel ───────────────────────────────────────────────────────── -->
<div class="sc-panel" [class.sc-panel--open]="isOpen">

  <!-- ── HEADER ── -->
  <div class="sc-header">
    <div class="sc-header-left">
      @if (view === 'history' && historyConv) {
        <button class="sc-back-btn" (click)="backToHistory()">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><polyline points="15 18 9 12 15 6"/></svg>
        </button>
      }
      @if (view === 'history' && !historyConv) {
        <button class="sc-back-btn" (click)="backToChat()">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><polyline points="15 18 9 12 15 6"/></svg>
        </button>
      }
      <div class="sc-header-title">
        <span>{{ view === 'history' ? (historyConv ? 'Past conversation' : 'Chat history') : 'Artisan Support' }}</span>
        @if (view === 'chat') {
          <span class="sc-agent-presence" [class.online]="agentOnline">
            <span class="sc-header-dot" [class.sc-header-dot--live]="agentOnline"></span>
            {{ agentOnline ? 'Online' : 'We\'ll reply soon' }}
          </span>
        }
      </div>
    </div>
    <div class="sc-header-actions">
      @if (view === 'chat') {
        <button class="sc-history-btn" (click)="showHistory()" title="Chat history">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <polyline points="12 8 12 12 14 14"/>
            <path d="M3.05 11a9 9 0 1 0 .5-4"/>
            <polyline points="3 3 3 8 8 8"/>
          </svg>
        </button>
      }
      <button class="sc-header-close" (click)="toggle()">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
        </svg>
      </button>
    </div>
  </div>

  <!-- ══ HISTORY VIEW ══ -->
  @if (view === 'history') {

    @if (!historyConv) {
      <div class="sc-body sc-history-body">
        @if (historyLoading) {
          <div class="sc-state"><div class="sc-spinner"></div></div>
        }
        @if (!historyLoading && historyList.length === 0) {
          <div class="sc-state sc-state--empty">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" width="40" height="40">
              <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
            </svg>
            <span>No past conversations</span>
          </div>
        }
        @for (c of historyList; track c.id) {
          <div class="sc-history-item" (click)="openHistoryConv(c)">
            <div class="sc-history-item-header">
              <span class="sc-history-date">{{ c.startedAt | date:'MMM d, y':'Asia/Jerusalem' }}</span>
              <span class="sc-history-status" [class.open]="c.status === 'open'" [class.closed]="c.status === 'closed'">{{ c.status }}</span>
            </div>
            <p class="sc-history-preview">{{ c.lastBody || 'No messages' }}</p>
          </div>
        }
      </div>
      <div class="sc-history-footer">
        <button class="sc-new-btn" (click)="startNew()">+ New conversation</button>
      </div>
    }

    @if (historyConv) {
      <div class="sc-body">
        <div class="sc-messages">
          <div class="sc-msg sc-msg--system">
            <span>Conversation from {{ historyConv.startedAt | date:'MMM d, y':'Asia/Jerusalem' }}</span>
          </div>
          @for (msg of historyMessages; track msg.id) {
            <div class="sc-msg"
                 [class.sc-msg--customer]="msg.senderType === 'customer'"
                 [class.sc-msg--agent]="msg.senderType === 'agent'"
                 [class.sc-msg--system]="msg.senderType === 'system'">
              <div class="sc-msg-bubble"><p class="sc-msg-body" dir="auto">{{ msg.body }}</p></div>
              <div class="sc-msg-meta">
                @if (msg.senderType === 'agent' && msg.respondedAs) {
                  <span>{{ msg.respondedAs }}</span>
                }
                <span>{{ msg.createdAt | date:'HH:mm':'Asia/Jerusalem' }}</span>
              </div>
            </div>
          }
          <div #messagesEnd></div>
        </div>
      </div>
      <div class="sc-expired">
        <p>This is a past conversation - read only.</p>
        <button class="blue-btn" (click)="startNew()">Start new chat</button>
      </div>
    }

  }

  <!-- ══ CHAT VIEW ══ -->
  @if (view === 'chat') {

    <div class="sc-body">
      @if (loading) {
        <div class="sc-state"><div class="sc-spinner"></div><span>Loading…</span></div>
      }
      @if (!loading) {
        <div class="sc-messages">
          <div class="sc-msg sc-msg--system"><span>Hi! How can we help you today?</span></div>

          @for (msg of messages; track msg.id) {
            <div class="sc-msg"
                 [class.sc-msg--customer]="msg.senderType === 'customer'"
                 [class.sc-msg--agent]="msg.senderType === 'agent'"
                 [class.sc-msg--system]="msg.senderType === 'system'">
              <div class="sc-msg-bubble">
                <p class="sc-msg-body" dir="auto">{{ msg.body }}</p>
                @for (att of getAttachmentsFor(msg.id); track att.id) {
                  <div class="sc-attachment" (click)="openAttachment(att)">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48"/></svg>
                    <span>{{ att.fileName }}</span>
                  </div>
                }
              </div>
              <div class="sc-msg-meta">
                @if (msg.senderType === 'agent' && msg.respondedAs) {
                  <span>{{ msg.respondedAs }}</span>
                }
                <span>{{ msg.createdAt | date:'HH:mm':'Asia/Jerusalem' }}</span>
              </div>
            </div>
          }
          <div #messagesEnd></div>
        </div>

        @if (isExpired) {
          <div class="sc-expired">
            <p>This conversation has ended.</p>
            <button class="blue-btn" (click)="startNew()">Start new chat</button>
          </div>
        }
      }
    </div>

    @if (!isExpired && !loading) {
      <div class="sc-composer">
        @if (pendingFile) {
          <div class="sc-file-preview">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48"/></svg>
            <span>{{ pendingFile.name }}</span>
            <button class="sc-file-clear" (click)="clearFile()">×</button>
          </div>
        }
        <div class="sc-composer-row">
          <textarea class="sc-textarea" [(ngModel)]="draft" (keydown)="onEnter($event)" (input)="autoGrow($event)" placeholder="Type a message…" rows="2" dir="auto" [disabled]="sending"></textarea>
          <label class="sc-attach-btn" title="Attach file">
            <input #fileInput type="file" hidden (change)="onFileSelected($event)" [disabled]="sending">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48"/></svg>
          </label>
          <button class="sc-send-btn" (click)="sendMessage()" [disabled]="(!draft.trim() && !pendingFile) || sending">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><line x1="22" y1="2" x2="11" y2="13"/><polygon points="22 2 15 22 11 13 2 9 22 2"/></svg>
          </button>
        </div>
        <p class="sc-composer-hint"><kbd>Enter</kbd> to send · <kbd>Shift</kbd>+<kbd>Enter</kbd> for a new line</p>
      </div>
    }

  }

</div>
